import { useEffect, useState } from "react";

/**
 * Hook para gerenciar altura do viewport em dispositivos mobile
 * Resolve problemas de altura em dispositivos com barra de navegação dinâmica
 */
export const useViewportHeight = () => {
  const [viewportHeight, setViewportHeight] = useState("100vh");

  useEffect(() => {
    const updateViewportHeight = () => {
      // Altura padrão
      let height = "100vh";

      // Para dispositivos mobile
      if (window.innerWidth <= 768) {
        // Tentar usar dvh (Dynamic Viewport Height) se suportado
        if (CSS.supports("height", "100dvh")) {
          height = "100dvh";
        } else {
          // Fallback para -webkit-fill-available (iOS Safari)
          height = "100vh";
        }
      }

      setViewportHeight(height);
    };

    // Atualizar na montagem
    updateViewportHeight();

    // Atualizar quando a orientação mudar
    const handleOrientationChange = () => {
      // Delay para aguardar a mudança de orientação
      setTimeout(updateViewportHeight, 100);
    };

    // Atualizar quando a janela for redimensionada
    const handleResize = () => {
      updateViewportHeight();
    };

    // Event listeners
    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("resize", handleResize);

    // Para iOS Safari - detectar mudanças na altura da viewport
    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        updateViewportHeight();
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        handleVisualViewportChange,
      );
    }

    // Cleanup
    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleVisualViewportChange,
        );
      }
    };
  }, []);

  return viewportHeight;
};

/**
 * Hook para detectar se está em dispositivo mobile
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  return isMobile;
};

/**
 * Hook para obter estilos de altura responsiva
 */
export const useResponsiveHeight = () => {
  const viewportHeight = useViewportHeight();
  const isMobile = useIsMobile();

  const getHeightStyles = (offset = 0) => {
    // Para desktop, usar altura fixa
    if (!isMobile) {
      const baseHeight = "100vh";

      if (offset === 0) {
        return {
          height: baseHeight,
          maxHeight: baseHeight,
          overflow: "hidden",
        };
      }

      return {
        height: `calc(${baseHeight} - ${offset}px)`,
        maxHeight: `calc(${baseHeight} - ${offset}px)`,
        overflow: "hidden",
      };
    }

    // Para mobile, usar altura dinâmica
    const baseHeight = viewportHeight;

    if (offset === 0) {
      return {
        height: baseHeight,
        minHeight: baseHeight,
      };
    }

    return {
      height: `calc(${baseHeight} - ${offset}px)`,
      minHeight: `calc(${baseHeight} - ${offset}px)`,
    };
  };

  return {
    viewportHeight,
    isMobile,
    getHeightStyles,
  };
};
